import { IconLayer } from "@deck.gl/layers"
import { UserLocation } from "@nova/schemas"
import { selectWorkspaceMembers } from "features/entity-caching/members/UserManagementSlice"
import { selectLiveUserLocationShares } from "features/entity-caching/flights/selectors"
import { userLocationStore } from "features/user-locations/user-location-store"
import { useAppSelector } from "integrations/redux/hooks"
import { useMemo } from "react"
import {
    getBroadcastStatus,
    USER_LOCATION_ICON_SIZE,
    userLocationIconStore,
    useUserLocationIconsVersion,
} from "./userLocationIconStore"

type UserLocationIconData = UserLocation & { iconUrl: string }

export const UserLocationLayers = ({ data }: { data: UserLocationIconData[] }) => [
    new IconLayer<UserLocationIconData>({
        id: "user-locations",
        data,
        pickable: false,
        getPosition: (d) => [d.longitude, d.latitude],
        getIcon: (d) => ({
            url: d.iconUrl,
            width: USER_LOCATION_ICON_SIZE,
            height: USER_LOCATION_ICON_SIZE,
            anchorX: USER_LOCATION_ICON_SIZE / 2,
            anchorY: USER_LOCATION_ICON_SIZE / 2,
        }),
        getAngle: (d) => (d.heading == null ? 0 : -d.heading),
        sizeUnits: "pixels",
        sizeScale: 1,
        billboard: false,
        getSize: () => 48,
    }),
]

export const useUserLocationLayers = () => {
    const locationsByUserId = userLocationStore.useAll()
    const members = useAppSelector(selectWorkspaceMembers)
    const iconsVersion = useUserLocationIconsVersion()
    const liveShares = useAppSelector(selectLiveUserLocationShares)

    return useMemo(() => {
        void iconsVersion // recompute on change
        const data = liveShares
            .filter((share) => share.visible && share.createdBy?.userId)
            .map((share) => locationsByUserId.get(share.createdBy!.userId))
            .filter((location): location is UserLocation => !!location)
            .map((location) => {
                const status = getBroadcastStatus(location.expiresAt)
                if (status === "ended") return null
                const member = members?.find((m) => m.userId === location.userId)
                const iconUrl = userLocationIconStore.get({
                    userId: location.userId,
                    fullName: member?.fullName,
                    profilePhotoS3Key: member?.profilePhotoS3Key,
                    status,
                })
                return { ...location, iconUrl }
            })
            .filter((location): location is UserLocationIconData => !!location)
        return UserLocationLayers({ data })
    }, [locationsByUserId, members, iconsVersion, liveShares])
}
